import React, { useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { Modal, Button } from "react-bootstrap";
import { Mail } from "react-feather";
import toast from "react-hot-toast";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import StockDocumentPreview from "./StockDocumentPreview";
import { useEmailLpoMutation } from "../../stockApi";

// Live preview (react-bootstrap Modal, same as CustomerSitesPanel.jsx) +
// real Print action. Print reuses this project's established popup pattern
// (qrImageUtils.js's printComposedImage: window.open("", "_blank") -> write
// HTML -> window.print()) rather than adding a PDF/print npm dependency -
// the popup's content is generated from the SAME <StockDocumentPreview>
// component via ReactDOMServer.renderToStaticMarkup, so there is exactly one
// template to maintain, not one for preview and a second for print.
//
// `copies` (LPO only) prints SUPPLIER COPY then OFFICE COPY back to back,
// matching the two-page sample PDF, with a page-break between them.
// Renamed from "CUSTOMER COPY" 2026-08-25 (handoff §27) - an LPO's
// counterparty is a supplier, not a customer; "customer" was carried over
// from the customer-facing document template this layout was modeled on.
//
// eMail (LPO only, enableEmail+lpoRecordGuid supplied) - a genuinely
// different rendering path from Print: it captures the on-screen Supplier
// Copy node with html2canvas and embeds that image into a real jsPDF
// document (the Office Copy is never touched/sent - the supplier should
// never see internal fields). No server-side PDF generation exists for
// LPO at all; the API only relays whatever bytes it's given to the
// branch's own SMTP settings.
const StockDocumentPrintModal = ({ show, onClose, docProps, copies = 1, enableEmail = false, lpoRecordGuid }) => {
  const copyLabels = copies === 2 ? ["SUPPLIER COPY", "OFFICE COPY"] : [null];
  const supplierCopyRef = useRef(null);
  const [isEmailing, setIsEmailing] = useState(false);
  const [emailLpo] = useEmailLpoMutation();
  const supplierEmail = docProps?.supplierEmail || "";

  const handlePrint = () => {
    const bodyHtml = copyLabels
      .map(
        (copyLabel, idx) =>
          `<div style="${idx > 0 ? "page-break-before: always;" : ""}">${ReactDOMServer.renderToStaticMarkup(
            <StockDocumentPreview {...docProps} copyLabel={copyLabel} />
          )}</div>`
      )
      .join("");

    const win = window.open("", "_blank", "width=900,height=1000");
    if (!win) return;
    win.document.write(
      `<html><head><title>${docProps.title} - ${docProps.docNo}</title>` +
        // A4 paper, not the browser's default page size - margin here plus
        // the template's own 16px padding is what actually meets the paper
        // edge; body width capped to the printable A4 area (210mm minus
        // margins) so the layout paginates the same on screen and on paper.
        `<style>@page { size: A4; margin: 10mm; } html,body { margin:0; padding:0; } body { width: 190mm; }</style>` +
        `</head>` +
        `<body onload="window.print();window.close();">${bodyHtml}</body></html>`
    );
    win.document.close();
  };

  const handleEmail = async () => {
    if (!supplierEmail) {
      toast.error("This supplier has no email address on file.");
      return;
    }
    setIsEmailing(true);
    try {
      // Capture the Supplier Copy exactly as already rendered on screen -
      // no separate off-screen render, so what gets emailed is guaranteed
      // to match what the preview shows.
      const canvas = await html2canvas(supplierCopyRef.current, { scale: 2 });
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;
      pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, imgHeight);
      const pdfBlob = pdf.output("blob");

      await emailLpo({ recordGuid: lpoRecordGuid, toAddress: supplierEmail, file: pdfBlob }).unwrap();
      toast.success(`Emailed to ${supplierEmail}`);
    } catch (error) {
      toast.error(error?.message || "Could not send the email.");
    } finally {
      setIsEmailing(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Print Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "#e9ecef" }}>
        {copyLabels.map((copyLabel, idx) => (
          <div key={idx} className="mb-3" style={{ border: "1px solid #ccc" }} ref={idx === 0 ? supplierCopyRef : undefined}>
            <StockDocumentPreview {...docProps} copyLabel={copyLabel} />
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer className="d-flex flex-wrap align-items-center gap-2">
        {enableEmail && (
          <div className="d-flex align-items-center gap-2 me-auto">
            <span className="text-muted small">{supplierEmail || "No email on file"}</span>
            <Button variant="outline-primary" size="sm" onClick={handleEmail} disabled={isEmailing || !supplierEmail}>
              <Mail size={14} className="me-1" />
              {isEmailing ? "Sending..." : "eMail"}
            </Button>
          </div>
        )}
        <Button variant="secondary" onClick={onClose}>Close</Button>
        <Button variant="primary" onClick={handlePrint}>Print</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StockDocumentPrintModal;
